import React from "react";
import { useParams, useHistory } from "react-router-dom";
import { Typography, Button } from "@material-ui/core";
import { useQuestionnaire, usePublishCheck, usePublishQuestionnaire } from "./queries";
import { usePersistedPreviewAttrs } from "./previewAttrsStorage";
import { useLocaleDiffPreviews } from "./useLocaleDiffPreviews";
import type { SnapshotRef } from "./useLocaleDiffPreviews";
import DiffShell from "./components/DiffShell";

const Review: React.VFC = () => {
  const { id: idStr } = useParams<{ id: string }>();
  const id = parseInt(idStr, 10);
  const history = useHistory();

  const questionnaireQuery = useQuestionnaire(id);
  const publishCheckQuery = usePublishCheck(id);
  const publishMutation = usePublishQuestionnaire();

  const [attrs, setAttrs] = usePersistedPreviewAttrs();

  const conflict = publishCheckQuery.data ?? null;
  const before: SnapshotRef | null = conflict
    ? { kind: "published", id: conflict.active_published_id }
    : null;
  const after: SnapshotRef | null = id ? { kind: "draft" } : null;

  const { previewsLoading } = useLocaleDiffPreviews({
    before,
    after,
    draftId: id || null,
    attrs,
  });

  const handlePublish = () => {
    publishMutation.mutate(id, {
      onSuccess: ({ id: newId }) => {
        history.push(`/health-forms/published/${newId}`);
      },
    });
  };

  const handleCancel = () => {
    history.push(`/health-forms/questionnaires/${id}`);
  };

  const name = questionnaireQuery.data?.name ?? "...";

  const subtitle = (
    <>
      {conflict && (
        <Typography variant="body2" color="textSecondary">
          Replacing version published {new Date(conflict.active_published_at).toLocaleString()} by {conflict.active_published_by}
        </Typography>
      )}
      {questionnaireQuery.data?.name && (
        <Typography variant="body2">
          <Button
            size="small"
            onClick={() => history.push(`/health-forms/questionnaires/${encodeURIComponent(questionnaireQuery.data!.name!)}/compare`)}
          >
            Compare against other versions
          </Button>
        </Typography>
      )}
    </>
  );

  const actions = (
    <>
      <Button variant="outlined" onClick={handleCancel}>Cancel</Button>
      <Button
        variant="contained"
        color="primary"
        onClick={handlePublish}
        disabled={publishCheckQuery.isLoading || previewsLoading || publishMutation.isLoading}
      >
        {publishMutation.isLoading ? "Publishing..." : "Publish"}
      </Button>
    </>
  );

  return (
    <DiffShell
      title={`Review: ${name}`}
      subtitle={subtitle}
      before={before}
      after={after}
      draftId={id || null}
      attrs={attrs}
      onAttrsChange={setAttrs}
      previousLabel="Currently published"
      nextLabel="About to publish"
      emptyMessage="No prior published version with this name. This will create the first snapshot."
      actions={actions}
      isLoading={publishCheckQuery.isLoading}
      error={publishCheckQuery.error ? "Failed to load publish check" : null}
    />
  );
};

export default Review;
